import {memo} from 'react';
import type {ReactNode} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import SectionHeader from '@/components/Common/SectionHeader';
import NoteBlock from '@/components/Common/NoteBlock';
import type {NoteBlockTone} from '@/components/Common/NoteBlock';
import {borderRadius, colors, fontSize, fontWeight, lineHeight, shadows, spacing, transition} from '@/styles/tokens';
import {iconTileLightSx} from '@/styles/iconTile';

export interface NoteBlockCardBlock {
    label: string;
    text: string;
    tone?: NoteBlockTone;
}

export interface NoteBlockCard {
    /** Icon element for the tile. Attached on the page so data files stay JSX-free. */
    icon?: ReactNode;
    title: string;
    /** The labelled blocks, in order — typically the problem then the answer. */
    blocks: NoteBlockCardBlock[];
}

export interface NoteBlockCardsProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    cards: NoteBlockCard[];
    /** Cards per row on desktop. Defaults to 3. */
    columns?: 2 | 3;
}

const gridItemSx = {display: 'flex'} as const;

const cardSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    width: '100%',
    height: '100%',
    bgcolor: colors.background.default,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius['3xl'],
    p: {xs: spacing[20], md: spacing[24]},
    transition: `box-shadow ${transition.medium}, border-color ${transition.medium}`,
    '&:hover': {
        boxShadow: shadows.elevated,
        borderColor: colors.primary.main
    }
} as const;

const titleSx = {
    mt: spacing[20],
    mb: spacing[16],
    fontSize: fontSize[16],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: lineHeight[24]
} as const;

// The blocks column carries the gap; `flex: 1` on the last block's wrapper is what
// pushes the closing block to the card foot so paired cards align.
const blocksWrapSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[16],
    flex: 1
} as const;

const lastBlockSx = {mt: 'auto'} as const;

/**
 * NoteBlockCards — one card per issue: icon, title, then labelled tinted blocks
 * stating the problem and the answer to it.
 *
 * Deliberately not a mode on `ChallengeGrid`, which renders its `details` as plain
 * labelled text and inverts the whole card to navy on hover — tinted inner blocks
 * would break under that inversion, so supporting both would mean two coupled
 * visual modes in one component.
 */
const NoteBlockCards = ({eyebrow, heading, subheading, cards, columns = 3}: NoteBlockCardsProps) => (
    <MUIContainer>
        <SectionHeader eyebrow={eyebrow} heading={heading} subheading={subheading} />

        <Grid container spacing={3}>
            {cards.map((card) => (
                <Grid item xs={12} sm={6} md={columns === 2 ? 6 : 4} key={card.title} sx={gridItemSx}>
                    <Box sx={cardSx}>
                        {card.icon && <Box sx={iconTileLightSx}>{card.icon}</Box>}
                        <Typography component="h3" className="font-headers" sx={titleSx}>
                            {card.title}
                        </Typography>
                        <Box sx={blocksWrapSx}>
                            {card.blocks.map((block, index) => (
                                <NoteBlock
                                    key={block.label}
                                    label={block.label}
                                    text={block.text}
                                    tone={block.tone}
                                    sx={index === card.blocks.length - 1 && card.blocks.length > 1 ? lastBlockSx : undefined}
                                />
                            ))}
                        </Box>
                    </Box>
                </Grid>
            ))}
        </Grid>
    </MUIContainer>
);

export default memo(NoteBlockCards);
